import { Component } from "@angular/core";
import { MessageService } from "primeng/api";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import {
  InsiderRelationship,
  InsiderRelationshipMap,
  InsiderRelationshipMappingFilter,
  InsiderRelationshipToMap,
} from "src/api/models";
import { InsiderRelationshipMappingService } from "src/api/services";
import {
  handlePasteWithoutLeadingSpaces,
  preventLeadingSpaceKey,
} from "src/core/utils/input-validation.utils";
enum FormType {
  ALL = "All",
  PARENT = "Parent",
  CHILD = "Child",
  NONE = "None",
}
@Component({
  selector: "app-update-insider-relationship",
  // imports: [],
  templateUrl: "./update-insider-relationship.component.html",
  styleUrl: "./update-insider-relationship.component.css",
  standalone: false,
})
export class UpdateInsiderRelationshipComponent {
  selectedInsider: any = null;
  readonly paginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };
  readonly childPaginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };

  unprocessedInsiderRelationship: any[] = [];
  unprocessedChildInsiderRelationship: any[] = [];
  selectedParentRow: any = null;
  isChildSearchTriggered: boolean = false;
  selectedChildRow: any = null;
  selectedDialogRow: any = null;
  formTypes: FormType[] = Object.values(FormType);
  // Pagination for Security
  first = 0;
  rows = 10;
  childFirst = 0;
  childRows = 10;
  searchByParentName?: string;
  selectedColorType?: FormType | undefined;
  searchByChildName?: string;
  reason?: string;
  isLoading: boolean = false;
  isChildLoading: boolean = false;
  isParentChecked: boolean = false;
  isChildChecked: boolean = false;
  relationshipOptions: { label: string; value: string }[] = [];
  constructor(
    private _messageService: MessageService,
    private _insiderRelationshipMappingService: InsiderRelationshipMappingService,
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef
  ) {}
  ngOnInit() {
    const insider = this.normalizeInsider(this.config.data.insiders);
    this.unprocessedInsiderRelationship = [insider];
    this.unprocessedChildInsiderRelationship = [insider];
    this.selectedInsider = this.config.data.insiders;
    this.relationshipOptions = InsiderRelationship.values().map((v) => ({
      label: v,
      value: v,
    }));
  }

  /**
   * Normalize insider objects so that comment fields are consistently available
   * on the properties that the template binds to.
   */
  private normalizeInsider(insider: any): any {
    if (!insider) return insider;

    return {
      ...insider,
      insiderComment:
        insider.insiderComment ?? insider.parentInsiderComment ?? insider.comment ?? null,
      childInsiderComment:
        insider.childInsiderComment ?? insider.insiderComment ?? insider.comment ?? null,
    };
  }
  onParentCheckboxChange(insider: any) {
    if (
      this.selectedParentRow?.parentInsiderId === insider.parentInsiderId &&
      this.selectedParentRow?.insiderId === insider.insiderId
    ) {
      // Already selected, so deselect
      this.selectedParentRow = null;
    } else {
      // Select new one
      this.selectedParentRow = insider;
    }
  }

  onChildCheckboxChange(insider: any) {
    if (
      this.selectedChildRow?.childInsiderId === insider.childInsiderId &&
      this.selectedChildRow?.insiderId === insider.insiderId
    ) {
      // Already selected, so deselect
      this.selectedChildRow = null;
    } else {
      // Select new one
      this.selectedChildRow = insider;
    }
  }

  loadData(event: any) {
    if (event.first === 0 && !event.forceLoad) {
      return;
    }
    if (event.first > 0 && this.paginator.pageSize != event.rows) {
      this.paginator.pageNumber = 1;
      this.first = 0;
    } else {
      this.paginator.pageNumber = event.first / event.rows + 1;
      this.first = event.first;
    }
    this.paginator.pageSize = event.rows;
    this.getUnprocessedInsiderRelationship();
  }
  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }
  onSearch() {
    this.first = 0;
    this.paginator.pageNumber = 1;
    this.getUnprocessedInsiderRelationship();
  }
  getUnprocessedInsiderRelationship() {
    const params: InsiderRelationshipMappingFilter = {
      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      relation: this.selectedColorType || "All",
      searchByName: this.searchByParentName,
    };
    this.isLoading = true;
    this._insiderRelationshipMappingService.getParentInsider(params).subscribe({
      next: (value) => {
        this.unprocessedInsiderRelationship = value ?? [];
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.paginator.totalCount =
          this.unprocessedInsiderRelationship?.[0]?.totalCount ?? 0;
        this.isLoading = false;
      },
    });
  }
  getChildInsiderRelationship() {
    const params: InsiderRelationshipMappingFilter = {
      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      searchByName: this.searchByChildName || undefined,
      insiderIdToSkip:
        this.selectedParentRow?.insiderId ||
        this.selectedParentRow?.parentInsiderId ||
        null,
    };
    this.isChildLoading = true;
    this._insiderRelationshipMappingService.getChildInsider(params).subscribe({
      next: (value) => {
        // Normalize so child comments from API are always visible as
        // `childInsiderComment` in the Update popup.
        this.unprocessedChildInsiderRelationship = (value ?? []).map((row: any) =>
          this.normalizeInsider(row)
        );
      },
      error: (err) => {
        this.isChildLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.childPaginator.totalCount =
          this.unprocessedChildInsiderRelationship?.[0]?.totalCount ?? 0;
        this.isChildLoading = false;
      },
    });
  }
  loadChildData(event: any) {
    if (!this.isChildSearchTriggered) return;
    if (event.first > 0 && this.childPaginator.pageSize != event.rows) {
      this.childPaginator.pageNumber = 1;
      this.childFirst = 0;
    } else {
      this.childPaginator.pageNumber = event.first / event.rows + 1;
      this.childFirst = event.first;
    }
    this.childPaginator.pageSize = event.rows;
    this.getChildInsiderRelationship();
  }
  childPageChange(event: any) {
    this.childFirst = event.first;
    this.childRows = event.rows;
  }
  onChildSearch() {
    if (!this.selectedParentRow) {
      this._messageService.add({
        summary: "Please select a parent record first.",
        severity: "warn",
      });
      return;
    }
    this.childFirst = 0;
    this.childPaginator.pageNumber = 1;
    this.getChildInsiderRelationship();
  }
  onUpdateClick() {
    if (this.selectedChildRow && (!this.reason || this.reason.trim() === "")) {
      this._messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "Reason is required when child rows are selected.",
      });
      return;
    }

    const params: InsiderRelationshipMap = {
      // Child insider basic info
      childInsiderId:
        this.selectedChildRow.childInsiderId || this.selectedChildRow.insiderId,
      childInsiderName:
        this.selectedChildRow.childInsiderName ||
        this.selectedChildRow.insiderName,
      // Child insider comment (from child grid textarea)
      childInsiderComment:
        this.selectedChildRow?.childInsiderComment ||
        this.selectedChildRow?.insiderComment ||
        null,
      insiderRelationshipId: this.selectedInsider.insiderRelationshipId,
      // Parent insider basic info
      parentInsiderId:
        this.selectedParentRow?.parentInsiderId ||
        this.selectedParentRow?.insiderId ||
        null,
      parentInsiderName:
        this.selectedParentRow?.parentInsiderName ||
        this.selectedParentRow?.insiderName ||
        "",
      // Parent insider comment (from parent grid textarea)
      insiderComment:
        this.selectedParentRow?.insiderComment ??
        this.selectedInsider?.insiderComment ??
        null,
      reason: this.reason ?? (null as unknown as string),
      totalCount: 1,
    };
    this.isLoading = true;
    this._insiderRelationshipMappingService
      .updateInsiderRelationship(params)
      .subscribe({
        next: (value) => {
          this._messageService.add({
            summary: "Mapping successful",
            severity: "success",
          });

          //  Selection clear
          this.searchByChildName = undefined;
          this.searchByParentName = undefined;
          this.selectedColorType = undefined;
          this.selectedParentRow = null;
          this.selectedChildRow = null;
          this.reason = undefined;
        },
        error: (err) => {
          this.isLoading = false;
          let errorMessage = "An unexpected error occurred";

          try {
            // If backend returns a JSON string, parse it
            const parsedError = JSON.parse(err.error);
            errorMessage = parsedError.message || errorMessage;
          } catch {
            // If parsing fails, fallback to direct message or text
            errorMessage = err.error?.message || err.error || errorMessage;
          }
          this._messageService.add({
            summary: "Mapping failed",
            detail: errorMessage,
            severity: "error",
          });
        },
        complete: () => {
          this.isLoading = false;
          this.ref.close({ refresh: true });
        },
      });
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }
  // One universal paste handler for ALL fields
  onPaste(
    event: ClipboardEvent,
    field: "searchByChildName" | "searchByParentName"
  ): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      this[field] = newValue;
    });
  }
}
